import { ButtonVariant } from '@/common/components/Button/Button'
import useOrganizationDocuments from '../hooks/useOrganizationDocuments'
import { FileUploadButton } from '@/common'
import { TrashIcon } from '@heroicons/react/24/solid'
import { useCallback } from 'react'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

export default function OrganizationDocumentUpload({
  label,
  disabled = false,
  fileName,
  onDocumentUploaded,
  onDocumentDeleted,
  onUploading,
  buttonVariant = 'default'
}: {
  label: string
  disabled?: boolean
  onDocumentUploaded: (url: string) => void
  onDocumentDeleted: (fileName: string) => void
  fileName: string | null
  onUploading?: (isUploading: boolean) => void
  buttonVariant?: ButtonVariant
}) {
  const { t } = useTranslation()
  const { uploadDocument, documentUploadState, downloadDocument, resetDocumentUploadState } =
    useOrganizationDocuments()

  const isSuccess = documentUploadState === 'success'

  const onTextClick = useCallback(async () => {
    if (fileName) {
      const result = await downloadDocument(fileName)

      if (!result.success) {
        console.error(result.errorMessage)
        return toast.error(result.errorMessage ?? t('common:errors.downloadFile.generic'))
      }
      toast.success(t('common:notifications.downloadSuccess'))
    }
  }, [downloadDocument, fileName, t])

  const onFileSelected = useCallback(
    async (file?: File) => {
      if (!file) {
        return
      }

      onUploading?.(true)

      const result = await uploadDocument(file)

      if (!result.success) {
        console.error(result.errorMessage)
        toast.error(result.errorMessage ?? t('common:errors.uploadFile.generic'))
      } else {
        onDocumentUploaded(result.fileName ?? '')
      }

      onUploading?.(false)
    },
    [uploadDocument, onDocumentUploaded, t, onUploading]
  )

  const onTrashClick = useCallback(() => {
    if (fileName) {
      onDocumentDeleted(fileName)
      resetDocumentUploadState()
    }
  }, [fileName, onDocumentDeleted, resetDocumentUploadState])

  return (
    <div className="w-fit">
      {isSuccess || fileName ? (
        <div className="flex justify-between gap-2 text-sm text-blue-500 overflow-hidden text-ellipsis bg-blue-300 rounded-md py-2.5 px-4">
          <button onClick={onTrashClick}>
            <TrashIcon className="size-4 text-blue-500" />
          </button>
          <button onClick={onTextClick}>
            <p className="underline">{fileName}</p>
          </button>
        </div>
      ) : (
        <FileUploadButton
          disabled={disabled}
          onChange={evt => onFileSelected(evt.target.files?.[0])}
          className="w-fit items-center"
          isLoading={documentUploadState === 'uploading'}
          variant={buttonVariant}
        >
          {label}
        </FileUploadButton>
      )}
    </div>
  )
}
